module Hereabout.UI.Components.EventPreviewComponent where

import Prelude

import Data.DateTime (DateTime(..), exactDate, Month(..))
import Data.Enum (toEnum)
import Data.Formatter.DateTime as DT
import Data.List as L
import Data.Maybe (Maybe(..))
import Data.Maybe as M
import DateTime.Format (formatDateTimeLocally)
import Effect.Class (class MonadEffect, liftEffect)
import Halogen (ClassName(..))
import Halogen as H
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Hereabout.Data.Route (Route(..), routeCodec)
import Hereabout.Effects (class Navigate, class ShareURL, shareUrl, navigate)
import Hereabout.Types (Event(..), EventType(..), Place(..), Venue(..))
import Hereabout.UI.Bits (renderIcon, renderIconWithTitle)
import Hereabout.UI.Bulma.Classes (cardContent)
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Components.ContentComponent as ContentComp
import Hereabout.UI.SemanticUI.Classes as SemUI
import Promise (then_)
import Routing.Duplex (print)
import Type.Proxy (Proxy(..))
import Web.DOM.Element (clientHeight, scrollHeight)
import Web.Event.Event (preventDefault)
import Web.Event.Internal.Types as WE
import Web.HTML.HTMLElement as HTMLE
import Web.UIEvent.MouseEvent as ME


data Action
  = InitComponent
  | NavigateToRoute WE.Event Route
  | ShareLink String

type Slot id = forall q. H.Slot q Void id

type State =
  { event :: Event
  , isExpanded :: Boolean
  }

type ChildSlots =
  ( contentComp :: ContentComp.Slot Unit
  )

component :: forall q o m. MonadEffect m => Navigate m => ShareURL m => H.Component q Event o m
component =
  H.mkComponent
    { initialState: \s -> { event : s, isExpanded : false }
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction, initialize = Just InitComponent }
    }

formatMinutes :: Int -> String
formatMinutes 1 = 
  "1 minute"
formatMinutes 60 = 
  "1 hour"
formatMinutes 61 = 
  "1 hour 1 minute"
formatMinutes x | x `mod` 60 == 0 && x > 0 = 
  show (x `div` 60)  <> " hours"
formatMinutes x | x > 60 && x `mod` 60 == 1 = 
  show (x `div` 60)  <> " hours 1 minute"
formatMinutes x | x > 60 = 
  show (x `div` 60)  <> " hours " <> show (x `mod` 60) <> " minutes"
formatMinutes x = 
  show x  <> " minutes"


renderPlace :: ∀ (s ∷ Type). Place -> HH.HTML s Action
renderPlace (GPSPosPlace gps) = 
  renderIcon "fa-location-crosshairs" $ HH.a [ ] [HH.text "Location"]
renderPlace (VenuePlace (Venue venue)) = 
  renderIcon "fa-building" $ HH.a [ HP.href (print routeCodec (VenueRoute venue.id)), HE.onClick \me -> NavigateToRoute (ME.toEvent me) (VenueRoute venue.id) ] [HH.text venue.name]

renderEventType ∷ forall m. EventType -> H.ComponentHTML Action ChildSlots m
renderEventType (EventType eventType) =
  HH.div [ HP.classes [ Bulma.tags ] ]
    [ HH.span [ HP.classes [ Bulma.tag ] ]
      [ case eventType.icon of
          Just icon ->
            HH.span [ HP.classes [ Bulma.iconText ] ]
              [ HH.span [ HP.classes [ Bulma.icon] ]
                [ HH.i [ HP.classes [ Bulma.fas, ClassName icon ] ] []
                ]
              , HH.span_ 
                [ HH.text eventType.name
                ]
              ]
          Nothing -> HH.text eventType.name
      ]
    ]

cardContentRef ∷ H.RefLabel
cardContentRef = H.RefLabel "card-content-ref"

render :: forall m. MonadEffect m => State -> H.ComponentHTML Action ChildSlots m
render s =
  HH.div [ HP.classes [ Bulma.card ], HP.style "height:100%; display:flex; flex-direction:column; max-height: 625px" ] $
    maybeImage <>
    [ HH.div [ HP.ref cardContentRef, HP.classes [ Bulma.cardContent ], HP.style "margin-bottom:auto; height:100%; overflow: hidden" ] $
      [ HH.p [ HP.classes [ Bulma.title, Bulma.mb3 ] ]
        [ HH.a [ HP.href (print routeCodec (EventRoute event.id)), HE.onClick \me -> NavigateToRoute (ME.toEvent me) (EventRoute event.id) ] 
          [ HH.text event.name
          ]
        ]
      , HH.p_ 
        [ renderIcon "fa-calendar" $ HH.text $ formatDateTimeLocally event.timestamp
        ]
      , HH.p_ 
        [ renderIcon "fa-hourglass" $ HH.text $ formatMinutes (event.duration `div` 60)
        ]
      , HH.p [ ]
        [ renderPlace (event.place)
        ]
      ] <> M.maybe [] (\signupUrl -> [ HH.p_ [ renderIcon "fa-users" $ HH.a [ HP.href signupUrl ] [ HH.text "Register" ] ] ]) event.signupUrl
        <>  [ HH.div [ HP.classes [ Bulma.tags, Bulma.mt2, Bulma.mb1 ] ]
              [ M.maybe (HH.div_ []) renderEventType event.type
              ]
            ]
        <>  [ HH.div [ HP.classes [ Bulma.block, Bulma.mt3 ] ] 
              [ HH.slot_ (Proxy :: _ "contentComp") unit ContentComp.component event.content
              ]
            ]
    , HH.a 
      [ HP.href (print routeCodec (EventRoute event.id))
      , HE.onClick \me -> NavigateToRoute (ME.toEvent me) (EventRoute event.id)
      , HP.classes $ showMoreVisisibleClasses <> [ Bulma.ml5 ] 
      ] [ HH.text "...show more" ] 
    , HH.footer [ HP.classes [ Bulma.cardFooter], HP.style "margin-top:auto" ]
      [ HH.div [ HP.classes [ Bulma.cardFooterItem, Bulma.isJustifyContentLeft ], HP.style "min-height:48px" ]
        [ renderIconWithTitle "fa-comments" "Comments" $ HH.text (show event.commentCount)
        , HH.span [ HP.classes [ Bulma.ml3 ] ] []
        , renderIconWithTitle "fa-star" "Favourites" $ HH.text (show event.favouriteCount)
        ]
        -- [ HH.a [ HE.onClick \_ -> ShareLink $ print routeCodec (EventRoute event.id) ] [ renderIcon "fa-share-from-square" $ HH.text "Share" ]
        -- ]
      ]
    ]
  where
    showMoreVisisibleClasses = 
      if s.isExpanded then
        []
      else 
        [ Bulma.isInvisible ]
    (Event event) = s.event
    maybeImage =
      case event.image of
        Just imSrc -> 
          [ HH.div [ HP.classes [ Bulma.cardImage ] ]
            [ HH.figure [ HP.classes [ Bulma.image, Bulma.is16By9 ] ] 
              [ HH.img [ HP.src imSrc, HP.classes [ H.ClassName "hereabout-clip" ]]
              ]
            ]
          ]
        Nothing -> []



handleAction :: forall cs o m. MonadEffect m => Navigate m => ShareURL m => Action -> H.HalogenM State Action cs o m Unit
handleAction = case _ of
  InitComponent -> do
    maybeCardContent <- H.getHTMLElementRef $ cardContentRef
    case maybeCardContent of
      Just cardContent -> do
        let cce = HTMLE.toElement cardContent
        sh <- liftEffect $ scrollHeight cce
        ch <- liftEffect $ clientHeight cce
        if (sh > ch) then
          H.modify_ _{ isExpanded = true }
        else
          pure unit
      Nothing -> pure unit
  NavigateToRoute e route -> do
    H.liftEffect $ preventDefault e
    H.lift $ navigate route
  ShareLink url -> do
    H.lift $ shareUrl url

